import React, { useState, useCallback, useRef } from "react";
import { Box, Alert, Text, Stack, Center } from "@mantine/core";
import { LoadingIndicator } from "../../generic/LoadingIndicator";

export interface IFrameContentBlockProps {
  /** URL of the HTML page to display */
  src: string | null | undefined;

  /** Title attribute for the iframe */
  title?: string;

  /** Additional CSS class name for styling */
  className?: string;

  /** Whether to allow scripts in the iframe (default: false for security) */
  allowScripts?: boolean;

  /** Custom sandbox permissions for the iframe */
  sandbox?: string;

  /** Callback fired when iframe loads successfully */
  onLoad?: (() => void) | undefined;

  /** Callback fired when iframe fails to load */
  onError?: ((error: string) => void) | undefined;
}

export const IFrameContentBlock: React.FC<IFrameContentBlockProps> = ({
  src,
  title = "HTML content",
  className,
  allowScripts = true,
  sandbox,
  onLoad,
  onError,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Generate sandbox attribute value
  const getSandboxValue = (): string => {
    if (sandbox) {
      return sandbox;
    }

    // Default secure sandbox permissions
    const defaultPermissions = [
      "allow-same-origin",
      "allow-popups",
      "allow-popups-to-escape-sandbox",
      "allow-forms",
    ];

    if (allowScripts) {
      defaultPermissions.push("allow-scripts");
    }

    return defaultPermissions.join(" ");
  };

  const handleIFrameLoad = useCallback(() => {
    try {
      // Check if the iframe actually loaded content
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        setLoading(false);
        setError(null);
        onLoad?.();
      }
    } catch (err) {
      // Cross-origin restrictions might prevent access to contentWindow
      // In this case, we'll assume the iframe loaded successfully
      setLoading(false);
      setError(null);
      onLoad?.();
    }
  }, [onLoad]);

  const handleIFrameError = useCallback(() => {
    setLoading(false);
    const errorMessage = "Failed to load HTML page";
    setError(errorMessage);
    onError?.(errorMessage);
  }, [onError]);

  // Validate URL
  if (!src || !src.trim()) {
    return (
      <Box
        className={`iframe-content-block iframe-content-block--invalid ${
          className || ""
        }`}
        h="100%"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--mantine-spacing-lg)",
        }}
      >
        <Stack align="center" gap="md" maw={400}>
          <Alert
            color="yellow"
            title="Invalid URL"
            className="iframe-content-block__invalid-alert"
          >
            <Text
              size="sm"
              className="iframe-content-block__invalid-message"
              ta="center"
            >
              No URL provided for the HTML page.
            </Text>
          </Alert>
        </Stack>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box
        className={`iframe-content-block iframe-content-block--error ${
          className || ""
        }`}
        h="100%"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--mantine-spacing-lg)",
        }}
      >
        <Stack align="center" gap="md" maw={400}>
          <Alert
            color="red"
            title="Loading Error"
            className="iframe-content-block__error-alert"
          >
            <Text
              size="sm"
              className="iframe-content-block__error-message"
              ta="center"
            >
              Unable to load the HTML page. Please check the URL and try again.
            </Text>
          </Alert>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      className={`iframe-content-block ${className || ""}`}
      h="100%"
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
      }}
    >
      {/* Loading indicator */}
      {loading && (
        <Center
          className="iframe-content-block__loading"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            backgroundColor: "var(--mantine-color-body)",
          }}
        >
          <Stack align="center" gap="md">
            <LoadingIndicator
              size="lg"
              className="iframe-content-block__loading-indicator"
            />
            <Text
              size="sm"
              c="dimmed"
              className="iframe-content-block__loading-text"
              ta="center"
            >
              Loading HTML page...
            </Text>
          </Stack>
        </Center>
      )}

      {/* IFrame */}
      <Box
        component="iframe"
        ref={iframeRef}
        src={src}
        title={title}
        sandbox={getSandboxValue()}
        onLoad={handleIFrameLoad}
        onError={handleIFrameError}
        className="iframe-content-block__iframe"
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          borderRadius: "var(--mantine-radius-sm)",
          opacity: loading ? 0 : 1,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
    </Box>
  );
};
